import { useCallback } from 'react';
import { gql, useMutation } from '@apollo/client';
import { get } from 'lodash';

import { graphQLIdGenerator, graphQLIdDecoder } from 'helpers/graphql-utils';
import { Steven } from 'helpers/steven';

import { REPORT_NODE_FRAGMENT } from './reportFragments.graphql';
import { CREATE_REPORT_NODE_BULK, CREATE_REPORT_NODE } from './reports.graphql';

export const REPORT_CREATE_FROM_TEMPLATE = gql`
  mutation ReportCreateFromTemplate($input: ReportCreateFromTemplateInput!) {
    reportCreateFromTemplate(input: $input) {
      report {
        id
        title
        subtitle
        createdAt
        projectIds
        reportNodes {
          nodes {
            ...ReportNode
          }
        }
      }
      errors {
        message
        path
        error
      }
    }
  }
  ${REPORT_NODE_FRAGMENT}
`;

const NOOP = () => {};

const databaseIdFrom = (obj) => parseInt(obj.databaseId, 10);
const imageNodeForId = (imageId) => {
  return graphQLIdGenerator('Image', imageId);
};

const useCreateReportFromTemplate = ({
  onSuccess = NOOP,
  onCreateError = NOOP,
}) => {
  const [reportCreateFromTemplate, { loading, error, data }] = useMutation(
    REPORT_CREATE_FROM_TEMPLATE,
    {
      onCompleted: (res) => {
        const errors = get(res, 'reportTemplateCreate.errors', null);
        if (errors && errors.length) {
          onCreateError(errors);
        }

        const {
          reportCreateFromTemplate: {
            report: { id },
          },
        } = res;

        const { databaseId: reportDbId } = graphQLIdDecoder(id);

        Steven.entityCrud({
          entity_id: reportDbId,
          entity_type: 'report',
          action: 'created',
          source: 'template',
        });
      },
      onError: (e) => {
        onCreateError(e);
      },
    },
  );

  const [createReportNodes] = useMutation(CREATE_REPORT_NODE_BULK, {
    onCompleted: (res) => {
      const errors = get(res, 'reportNodeCreatePhotosInBulk.errors', null);
      if (errors && errors.length) {
        onCreateError(errors);
      }
    },
    onError: (e) => {
      onCreateError(e);
    },
  });

  const [createSection] = useMutation(CREATE_REPORT_NODE, {
    onError: (e) => {
      onCreateError(e);
    },
  });

  const createReportFromTemplate = useCallback(
    ({ templateId, projectId, assets }) => {
      const handleAddPhotos = (reportId, sectionId) => {
        const selectedItems = get(assets, 'assetsByType.photos', []);
        const photoDatabaseIds = selectedItems.map(databaseIdFrom);
        const imageIds = photoDatabaseIds.map(imageNodeForId);

        createReportNodes({
          variables: {
            input: {
              id: reportId,
              position: 1,
              imageIds,
              parentId: sectionId,
            },
          },
        }).then(() => {
          onSuccess();
          Turbo.visit(`/reports/${reportId}/edit`);
        });
      };

      const handleCreateSection = (reportId) => {
        createSection({
          variables: {
            input: {
              type: 'ASSET_SECTION',
              name: 'Section 1',
              position: 2,
              reportId,
              data: {
                assetSection: {
                  summary: '',
                },
              },
            },
          },
        }).then((result) => {
          const newSection = get(
            result,
            'data.reportNodeCreate.reportNode' || null,
          );
          if (newSection) {
            handleAddPhotos(reportId, newSection.id);
          }
        });
      };

      reportCreateFromTemplate({
        variables: {
          input: {
            templateId,
            projectId,
          },
        },
      }).then((result) => {
        const newReport = get(
          result,
          'data.reportCreateFromTemplate.report',
          null,
        );
        if (newReport) {
          if (assets) {
            const templateNodes = get(newReport, 'reportNodes.nodes', []);
            const firstTemplateSection = templateNodes.find(
              (tem) => tem.position === 2,
            );
            const firstSectionId = get(firstTemplateSection, 'id', null);

            if (firstSectionId) {
              handleAddPhotos(newReport.id, firstSectionId);
            } else {
              // create a section first.
              handleCreateSection(newReport.id);
            }
          } else {
            onSuccess();
            Turbo.visit(`/reports/${newReport.id}/edit`);
          }
        }
      });
    },
    [reportCreateFromTemplate, onSuccess, createReportNodes, createSection],
  );

  return {
    createReportFromTemplate,
    loading,
    error,
    data,
  };
};

export default useCreateReportFromTemplate;
